import React from "react"
import { fitText, type FitResult } from "utilities/fitFontSize"

export type KioskTextFitConfig = {
  minScale: number
  fewerLinesMinScale: number
  maxLines: number
  fontWeight: number
}

// Event title (system UI font, medium weight per spec): shrink to keep one line down to
// ~84% of nominal; if it still doesn't fit, wrap to a 2nd line (at near-nominal). DOM-
// verified at the 15pt-margin width / medium weight: "Davos Leadership Forum" and "Stranger
// Things Premiere" stay one line, while "Netflix Cannes Lions 2026" wraps to two. Below the
// wrap it can shrink to the 60% floor.
export const KIOSK_TITLE_FIT: KioskTextFitConfig = {
  minScale: 0.6,
  fewerLinesMinScale: 0.885,
  maxLines: 2,
  fontWeight: 500,
}

/** Scan prompt: shrink ~15% to stay on one line, then wrap (≤2 lines). */
export const KIOSK_SCAN_LABEL_FIT: KioskTextFitConfig = {
  minScale: 0.5,
  fewerLinesMinScale: 0.85,
  maxLines: 2,
  fontWeight: 400,
}

/**
 * Fit `text` to `availableWidthPx` once, returning both the font size and the line count.
 * The font drives rendering; the line count lets the layout reserve the right height. When
 * `enabled` is false the text isn't shown, so we skip measuring and return the nominal size.
 */
export const useFittedText = (
  text: string,
  maxFontPx: number,
  availableWidthPx: number,
  config: KioskTextFitConfig,
  enabled = true,
): FitResult => React.useMemo(
  () => (enabled
    ? fitText({
      text, maxFontPx, availableWidthPx, ...config,
    })
    : { fontPx: maxFontPx, lines: 1 }),
  [text, maxFontPx, availableWidthPx, config, enabled],
)
